import * as React from 'react'
import moment from 'moment'
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'

import DriverViewControlBarLayout from './DriverViewControlBarLayout'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import useChangeDriverParams from '../../hooks/useChangeDriverParams'
import { useDate, useDateStore } from '@/modules/drivers/stores/dateStore'
import DatePickerSingleButton from '../datepickers/DatePickerSingleButton'
import MonthPickerButton from '../datepickers/MonthPickerButton'
import YearPickerButton from '../datepickers/YearPickerButton'
import DatePickerRangeButton from '../datepickers/DatePickerRangeButton'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import { useQueryDriver } from '@/lib/api/company/drivers'
import { DatePickerRangeProps } from '../../types'
import { DRIVER_ROUTES } from '../../routes-typed'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import { GroupModel } from '@/app/models/groupModel'
import ViewMode from './ViewMode'
import { TaskStatusWrapper } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const base = '/et/company/:companyId/driverModule'
const PATTERNS = {
  activities: `${DRIVER_ROUTES.activities.path}/*`,
  timesheet: `${DRIVER_ROUTES.groupTimesheet.path}/*`,
  amountsPreview: `${base}/amountsPreview/*`,
  foreignAmounts: `${base}/driver/:driverId/foreignAmounts/*`,
  schedule: `${DRIVER_ROUTES.schedule.path}/*`,
  businessTrip: `${base}/driver/:driverId/businessTrip/*`,
  newGroupedBusinessTrip: `${base}/driver/:driverId/newGroupedBusinessTrip/*`,
  violation: `${base}/driver/:driverId/violation/*`,
  violationNew: `${base}/driver/:driverId/violation-new/*`,
  violationsNew: `${base}/violations-new/*`,
  groupedBusinessTripsA: `${DRIVER_ROUTES.groupBusinessTrips.path.replace(/\/from\/.*/, '')}/*`,
  groupedBusinessTripsB: `${base}/groupedBusinessTrip/*`,
} as const

const useRouteFlags = () => {
  const isActivitiesRoute = !!useMatch(PATTERNS.activities)
  const isTimesheetRoute = !!useMatch(PATTERNS.timesheet)
  const isAmountsPreviewRoute = !!useMatch(PATTERNS.amountsPreview)
  const isForeignAmountsRoute = !!useMatch(PATTERNS.foreignAmounts)
  const isScheduleRoute = !!useMatch(PATTERNS.schedule)
  const isBusinessTripRoute = !!useMatch(PATTERNS.businessTrip)
  const isNewGroupedBusinessTripRoute = !!useMatch(
    PATTERNS.newGroupedBusinessTrip,
  )
  const isViolationRoute = !!useMatch(PATTERNS.violation)
  const isViolationNewRoute = !!useMatch(PATTERNS.violationNew)
  const isViolationsNewRoute = !!useMatch(PATTERNS.violationsNew)
  // Oba `useMatch` muszą polecieć zawsze - w zapisie `a || b` drugi hook wypadał
  // z renderu, gdy pierwszy wzorzec pasował.
  const matchesGroupedBusinessTripsA = !!useMatch(
    PATTERNS.groupedBusinessTripsA,
  )
  const matchesGroupedBusinessTripsB = !!useMatch(
    PATTERNS.groupedBusinessTripsB,
  )
  const isGroupedBusinessTripRoute =
    matchesGroupedBusinessTripsA || matchesGroupedBusinessTripsB

  return {
    isActivitiesRoute,
    isTimesheetRoute,
    isAmountsPreviewRoute,
    isForeignAmountsRoute,
    isScheduleRoute,
    isBusinessTripRoute,
    isNewGroupedBusinessTripRoute,
    isViolationRoute,
    isViolationNewRoute,
    isViolationsNewRoute,
    isGroupedBusinessTripRoute,
    usesMonthParam:
      isTimesheetRoute || isAmountsPreviewRoute || isForeignAmountsRoute,
  }
}

const toServiceDate = (d: DatePickerRangeProps['startDate']) =>
  moment(
    `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
    'YYYY-MM-DD',
    true,
  )

const DriverActivityBarWithDate: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { companyId, driverId, from, to } = useParams<{
    companyId: string
    driverId?: string
    from?: string
    to?: string
  }>()

  const { driversIds, setSelectedDriverId, groups, setActualGroup } =
    useDrivers()
  const {
    single: singleDate,
    month,
    year,
    setMonth,
    setYear,
    range,
    setRange,
    getDateRangeForView,
  } = useDate()
  const { data: driver } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: true,
  })
  const { goNextDriverInActivity, goPrevDriverInActivity, replaceUrlParam } =
    useChangeDriverParams()
  const { type, chartRange, setChartRange } = useActivity()

  const flags = useRouteFlags()
  const {
    isActivitiesRoute,
    isTimesheetRoute,
    isScheduleRoute,
    isBusinessTripRoute,
    isNewGroupedBusinessTripRoute,
    isViolationRoute,
    isViolationNewRoute,
    isViolationsNewRoute,
    isGroupedBusinessTripRoute,
    usesMonthParam,
  } = flags

  const pathDates = React.useMemo(() => {
    const m = location.pathname.match(/\/from\/([^/]+)\/to\/([^/]+)/)
    return m ? { from: m[1], to: m[2] } : null
  }, [location.pathname])
  const rangeFrom = from ?? pathDates?.from
  const rangeTo = to ?? pathDates?.to

  const isViolationsNewWithoutDates =
    isViolationsNewRoute && !rangeFrom && !rangeTo

  const isSyncingFromUrlRef = React.useRef(false)

  const requiresDriverId = !isGroupedBusinessTripRoute && !isViolationsNewRoute
  const hasRequiredParams = !!companyId && (!requiresDriverId || !!driverId)

  React.useEffect(() => {
    if (!isViolationsNewWithoutDates) return

    const { from: fromStr, to: toStr } = getDateRangeForView()
    if (!fromStr || !toStr) return

    navigate(
      `/et/company/${companyId}/driverModule/violations-new/from/${fromStr}/to/${toStr}`,
      { replace: true },
    )
  }, [
    isViolationsNewWithoutDates,
    range,
    companyId,
    navigate,
    getDateRangeForView,
  ])

  const needsRangeSync =
    isBusinessTripRoute ||
    isGroupedBusinessTripRoute ||
    isNewGroupedBusinessTripRoute ||
    isViolationRoute ||
    isViolationNewRoute ||
    isViolationsNewRoute
  React.useEffect(() => {
    if (!needsRangeSync || !rangeFrom || !rangeTo) return

    const fromDate = moment(rangeFrom, SERVICE_DATE_FORMAT, true)
    const toDate = moment(rangeTo, SERVICE_DATE_FORMAT, true)
    if (!fromDate.isValid() || !toDate.isValid()) return

    const newRange: DatePickerRangeProps = {
      startDate: {
        fullDate: format(fromDate.toDate(), 'DD-MM-YYYY'),
        day: fromDate.date(),
        month: fromDate.month() + 1,
        year: fromDate.year(),
      },
      endDate: {
        fullDate: format(toDate.toDate(), 'DD-MM-YYYY'),
        day: toDate.date(),
        month: toDate.month() + 1,
        year: toDate.year(),
      },
    }

    if (
      !range ||
      range.startDate?.fullDate !== newRange.startDate.fullDate ||
      range.endDate?.fullDate !== newRange.endDate.fullDate
    ) {
      setRange(newRange)
    }
  }, [rangeFrom, rangeTo, needsRangeSync])

  React.useEffect(() => {
    if (!usesMonthParam) return
    const monthMatch = location.pathname.match(/\/month\/([^/]+)/)
    if (!monthMatch) return
    const urlMonth = monthMatch[1]
    if (urlMonth === month) return

    isSyncingFromUrlRef.current = true
    setMonth(urlMonth)
    setTimeout(() => {
      isSyncingFromUrlRef.current = false
    }, 0)
  }, [location.pathname, usesMonthParam])

  React.useEffect(() => {
    const currentSingle = useDateStore.getState().single
    if (!currentSingle || !isActivitiesRoute) return
    const already =
      location.pathname.includes(`dailyActivity/${currentSingle}`) ||
      location.pathname.includes(`weeklyActivity/${currentSingle}`) ||
      location.pathname.includes(`details/${currentSingle}`)
    if (already) return

    replaceUrlParam({
      paramNames: ['dailyActivity', 'weeklyActivity', 'details'],
      newValue: currentSingle,
    })
  }, [singleDate, isActivitiesRoute])

  const prevMonthRef = React.useRef(month)
  React.useEffect(() => {
    const monthChanged = prevMonthRef.current !== month
    prevMonthRef.current = month
    if (!monthChanged) return
    if (!month || isSyncingFromUrlRef.current) return
    if (isGroupedBusinessTripRoute || isNewGroupedBusinessTripRoute) return

    let currentUrlMonth: string | null = null
    if (usesMonthParam) {
      const m = location.pathname.match(/\/month\/([^/]+)/)
      if (m) currentUrlMonth = m[1]
    } else if (isActivitiesRoute) {
      const m = location.pathname.match(/\/monthlyActivity\/([^/]+)/)
      if (m) currentUrlMonth = m[1]
    }
    if (currentUrlMonth === month) return

    if (usesMonthParam) {
      replaceUrlParam({ paramNames: ['month'], newValue: month })
    } else if (isActivitiesRoute) {
      replaceUrlParam({ paramNames: ['monthlyActivity'], newValue: month })
    }
  }, [month, usesMonthParam, isActivitiesRoute, isGroupedBusinessTripRoute])

  // Keep the URL year segment in sync with the store's `year` (see prevMonthRef).
  const prevYearRef = React.useRef(year)
  React.useEffect(() => {
    const yearChanged = prevYearRef.current !== year
    prevYearRef.current = year
    if (!year || !yearChanged) return
    if (isScheduleRoute && !location.pathname.includes(`year/${year}`)) {
      replaceUrlParam({ paramNames: ['year'], newValue: year })
    } else if (
      isActivitiesRoute &&
      !location.pathname.includes(`yearlyActivity/${year}`)
    ) {
      replaceUrlParam({ paramNames: ['yearlyActivity'], newValue: year })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, isScheduleRoute, isActivitiesRoute])

  const handleDateRangeChange = React.useCallback(
    (dateRange: DatePickerRangeProps) => {
      const acceptsRange =
        isBusinessTripRoute ||
        isGroupedBusinessTripRoute ||
        isNewGroupedBusinessTripRoute ||
        isViolationRoute ||
        isViolationNewRoute ||
        isViolationsNewRoute
      if (!acceptsRange || !dateRange.startDate || !dateRange.endDate) return

      const fromD = toServiceDate(dateRange.startDate)
      const toD = toServiceDate(dateRange.endDate)
      if (!fromD.isValid() || !toD.isValid()) return

      const fromStr = fromD.format(SERVICE_DATE_FORMAT)
      const toStr = toD.format(SERVICE_DATE_FORMAT)

      setRange(dateRange)

      const company = companyId
      const driver = driverId ? Number(driverId) : undefined

      let newPath: string
      if (isGroupedBusinessTripRoute) {
        newPath = DRIVER_ROUTES.groupBusinessTrips.buildPath({
          companyId: company,
          from: fromStr,
          to: toStr,
        })
      } else if (isNewGroupedBusinessTripRoute) {
        newPath = DRIVER_ROUTES.businessTripNew.buildPath({
          companyId: company,
          driverId: driver!,
          from: fromStr,
          to: toStr,
        })
      } else if (isViolationRoute) {
        newPath = DRIVER_ROUTES.violations.buildPath({
          companyId: company,
          driverId: driver!,
          from: fromStr,
          to: toStr,
        })
      } else if (isViolationNewRoute) {
        newPath = DRIVER_ROUTES.violationsNew.buildPath({
          companyId: company,
          driverId: driver!,
          from: fromStr,
          to: toStr,
        })
      } else if (isViolationsNewRoute) {
        newPath = DRIVER_ROUTES.groupViolationsNew.buildPath({
          companyId: company,
          from: fromStr,
          to: toStr,
        })
      } else {
        newPath = DRIVER_ROUTES.businessTrips.buildPath({
          companyId: company,
          driverId: driver!,
          from: fromStr,
          to: toStr,
        })
      }
      navigate(newPath)
    },
    [
      companyId,
      driverId,
      isBusinessTripRoute,
      isGroupedBusinessTripRoute,
      isNewGroupedBusinessTripRoute,
      isViolationRoute,
      isViolationNewRoute,
      isViolationsNewRoute,
      navigate,
      setRange,
    ],
  )

  const handleSelectGroup = React.useCallback(
    (option: GroupModel | null) => {
      if (!option || option.id === 0) {
        setActualGroup(null)
      } else if (option.id === -1) {
        setActualGroup({ id: -1, name: 'Aktywni' })
      } else if (option.id === -2) {
        setActualGroup({ id: -2, name: 'Nieaktywni' })
      } else {
        const group = groups?.find((g) => g.id === option.id)
        if (group) setActualGroup(group)
      }
    },
    [setActualGroup, groups],
  )

  // Te dwa hooki stały niżej, pod `return null` - przy braku parametrów trasy wypadały
  // z renderu i zmieniały kolejność hooków.
  const { activeViewMode } = useDrivers()
  const { t } = useLocalize()

  if (!hasRequiredParams) return null

  const sortedIds = [...(driversIds ?? [])].sort(
    (a: number, b: number) => a - b,
  )

  // Compose date-picker children based on the active route / view mode.
  const children: React.ReactNode[] = []

  if (usesMonthParam) {
    children.push(<MonthPickerButton key="month-tsh" useRomanNumerals />)
  }

  const singleDateRoute =
    !usesMonthParam &&
    (type === 'daily' || type === 'weekly' || type === 'details') &&
    !isBusinessTripRoute &&
    !isGroupedBusinessTripRoute &&
    !isNewGroupedBusinessTripRoute &&
    !isViolationRoute &&
    !isViolationNewRoute &&
    !isViolationsNewRoute

  if (singleDateRoute) {
    children.push(
      <DatePickerSingleButton
        key="single"
        iconAlign="left"
        size="small"
        label="Ustaw"
        clearable={false}
        confirmSelection={false}
      />,
    )
  }

  if (!usesMonthParam && type === 'monthly') {
    children.push(<MonthPickerButton key="month-act" useRomanNumerals />)
  }

  if (!usesMonthParam && type === 'yearly' && isActivitiesRoute) {
    children.push(
      <YearPickerButton key="year" value={year} handleChangeValue={setYear} />,
    )
  }

  if (!usesMonthParam && type === 'chartDetails' && isActivitiesRoute) {
    children.push(
      <DatePickerRangeButton
        key="chart-range"
        iconAlign="left"
        size="small"
        highlightSelected={true}
        onChange={(dateRange) => {
          if (!dateRange.startDate || !dateRange.endDate) return
          setChartRange(dateRange)
        }}
        initialDate={chartRange ?? undefined}
        clearable={false}
      />,
    )
  }

  const showRangePicker =
    isGroupedBusinessTripRoute ||
    isViolationsNewRoute ||
    isBusinessTripRoute ||
    isViolationRoute ||
    isViolationNewRoute
  if (showRangePicker) {
    children.push(
      <DatePickerRangeButton
        key="range"
        iconAlign="left"
        size="small"
        highlightSelected={true}
        onChange={handleDateRangeChange}
        initialDate={range}
        clearable={false}
      />,
    )
  }
  if (isGroupedBusinessTripRoute) {
    return (
      <TaskStatusWrapper>
        <div className="tw-relative tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-start tw-gap-2 tw-border-b md:tw-gap-4">
          <ViewMode padding />
          <div className="tw-text-nowrap tw-py-2 tw-font-medium tw-text-content-base">
            {activeViewMode === 'group'
              ? t('common.view-mode-group')
              : t('common.view-mode-single')}
          </div>
          <div className="tw-z-[200] tw-flex tw-w-full tw-flex-wrap-reverse tw-justify-end tw-gap-2 tw-px-4 tw-py-2 sm:tw-items-center sm:tw-justify-end sm:tw-gap-6">
            {children}
          </div>
        </div>
      </TaskStatusWrapper>
    )
  }

  if (isViolationsNewRoute) {
    return (
      <TaskStatusWrapper>
        <div className="tw-relative tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-start tw-gap-2 tw-border-b md:tw-gap-4">
          <ViewMode padding />
          <div className="tw-text-nowrap tw-py-2 tw-font-medium tw-text-content-base">
            {activeViewMode === 'group'
              ? t('common.view-mode-group')
              : t('common.view-mode-single')}
          </div>
          <div className="tw-z-[200] tw-flex tw-w-full tw-flex-wrap-reverse tw-justify-end tw-gap-2 tw-px-4 tw-py-2 sm:tw-items-center sm:tw-justify-end sm:tw-gap-6">
            {children}
          </div>
        </div>
      </TaskStatusWrapper>
    )
  }

  return (
    <DriverViewControlBarLayout
      companyId={companyId}
      blockedView={
        !isBusinessTripRoute &&
        !isNewGroupedBusinessTripRoute &&
        !isViolationNewRoute
      }
      fullName={driver?.fullName || ''}
      onPrev={() => {
        const id = goPrevDriverInActivity({
          sortedIds,
          numericId: Number(driverId),
        })
        setSelectedDriverId(id)
      }}
      onNext={() => {
        const id = goNextDriverInActivity({
          sortedIds,
          numericId: Number(driverId),
        })
        setSelectedDriverId(id)
      }}
    >
      {children}
    </DriverViewControlBarLayout>
  )
}

export default DriverActivityBarWithDate
